import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  SectionLabel,
  Spinner,
} from '@wundo/ui';
import { formatDateTime } from '@/utils/dateUtils.ts';
import { fetchUserSummary, getTemplate, type UserSummary } from '@/lib/broadcast.ts';
import type { BroadcastHistoryItem, Template } from '@/types/broadcast.ts';
import { BroadcastCounts } from './BroadcastCounts.tsx';
import { userSummaryName } from './userSummaryName.ts';
import { reportError } from '@/lib/reportError.ts';

export interface BroadcastDetailDialogProps {
  /** The broadcast being read; null when the panel is closed. */
  broadcast: BroadcastHistoryItem | null;
  onClose: () => void;
}

interface BroadcastDetail {
  users: UserSummary[];
  sentBy: UserSummary;
  /** null when the template has since been deleted. */
  template: Template | null;
}

async function loadDetail(broadcast: BroadcastHistoryItem): Promise<BroadcastDetail> {
  const [users, sentBy, template] = await Promise.all([
    Promise.all(broadcast.user_ids.map(fetchUserSummary)),
    fetchUserSummary(broadcast.sent_by),
    getTemplate(broadcast.template_name).catch((failure: unknown) => {
      reportError(failure, {
        where: 'BroadcastDetailDialog.template',
        templateName: broadcast.template_name,
      });
      return null;
    }),
  ]);

  return { users, sentBy, template };
}

function TemplateBody({
  loading,
  template,
}: {
  loading: boolean;
  template: Template | null | undefined;
}) {
  if (loading) {
    return <Spinner />;
  }
  if (!template) {
    return <p className='text-sm text-ink-muted'>Template no longer exists.</p>;
  }
  return (
    <p className='rounded-md bg-fill-faint p-3.5 text-sm whitespace-pre-wrap text-ink'>
      {template.body}
    </p>
  );
}

/**
 * One broadcast, opened out: what was sent, who sent it, who it reached.
 *
 * A broadcast record keeps ids alone, so the names are looked up when the panel
 * opens rather than carried in the list — forty lookups behind a click nobody
 * has made yet is forty requests for a table nobody reads that way.
 */
export function BroadcastDetailDialog({
  broadcast,
  onClose,
}: BroadcastDetailDialogProps) {
  const detail = useQuery({
    queryKey: ['broadcastDetail', broadcast?.id],
    queryFn: () => loadDetail(broadcast!),
    enabled: broadcast !== null,
  });

  return (
    <Dialog open={broadcast !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent size='lg' className='max-h-9/10 overflow-y-auto'>
        {broadcast !== null && (
          <>
            <DialogHeader>
              <DialogTitle>Broadcast Details</DialogTitle>
            </DialogHeader>

            <div className='flex flex-col gap-5 px-5 py-4'>
              <BroadcastCounts
                total={broadcast.total}
                sent={broadcast.sent}
                failed={broadcast.failed}
              />

              <div className='grid grid-cols-2 gap-3'>
                <div className='flex flex-col gap-1'>
                  <SectionLabel as='h3'>Sent by</SectionLabel>
                  {detail.data ? (
                    <Link
                      to='/users/$id'
                      params={{ id: detail.data.sentBy.id }}
                      onClick={onClose}
                      className='text-sm font-semibold text-ink'
                    >
                      {userSummaryName(detail.data.sentBy)}
                    </Link>
                  ) : (
                    <span className='text-sm font-semibold text-ink'>
                      {broadcast.sent_by.slice(0, 8)}
                    </span>
                  )}
                </div>
                <div className='flex flex-col gap-1'>
                  <SectionLabel as='h3'>Date</SectionLabel>
                  <span className='text-sm font-semibold text-ink'>
                    {formatDateTime(broadcast.created_at)}
                  </span>
                </div>
              </div>

              <div className='flex flex-col gap-1.5'>
                <h3 className='text-sm font-semibold text-ink'>
                  Template: {broadcast.template_name}
                </h3>
                <TemplateBody
                  loading={detail.isPending}
                  template={detail.data?.template}
                />
              </div>

              {broadcast.errors.length > 0 && (
                <div className='flex flex-col gap-1.5'>
                  <h3 className='text-sm font-semibold text-ink'>Errors</h3>
                  <ul className='flex flex-col gap-1'>
                    {broadcast.errors.map((error, index) => (
                      <li
                        /* Two identical lines can both be true; only the order tells them apart. */
                        key={`${error}-${index}`}
                        className='rounded-md bg-danger-fill px-2.5 py-1.5 text-sm text-danger'
                      >
                        {error}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className='flex flex-col gap-1.5'>
                <h3 className='text-sm font-semibold text-ink'>
                  Recipients ({broadcast.user_ids.length})
                </h3>
                {detail.isPending ? (
                  <Spinner />
                ) : (
                  <ul className='divide-y divide-line overflow-hidden rounded-md border border-line'>
                    {(detail.data?.users ?? []).map((user) => (
                      <li key={user.id}>
                        <Link
                          to='/users/$id'
                          params={{ id: user.id }}
                          onClick={onClose}
                          className='flex items-center justify-between px-3.5 py-2.5 hover:bg-fill'
                        >
                          <span className='text-sm text-ink'>
                            {userSummaryName(user)}
                          </span>
                          <span className='font-mono text-xs text-ink-muted'>
                            {user.id.slice(0, 8)}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button size='sm' variant='quiet' onClick={onClose}>
                Close
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
